import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { LineGraph, Scorecard, PieGraph, BarGraph } from '../components/utility/Charts';
import { StoreDateRangeFilter, fillDates } from '../components/utility/Utility';
import {
  storeApiBoundsFromMatch,
  storeMatchRangeLastNCalendarDays,
  storeMatchRangeThisWeek,
  storeMatchRangeLastWeek,
  storeMatchRangeThisMonth,
  storeMatchRangeLastMonth,
  storeMatchRangeThisYear,
  storeMatchRangeLastYear,
} from '../utils/storeDateRange';
import { useClientTimezone } from '../hooks/hooks';
import '../assets/css/charts.css';
import { ActionDescription, demoDashboardData, InitiateTutorial } from '../components/Onboarding';


import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
axios.defaults.withCredentials = true;

export default function Dashboard() {
    const { getJwtToken, userProfile } = useContext(AuthContext);
    const [events, setEvents] = useState(false);
    const [lineGraph, setLineGraph] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [activeRange, setActiveRange] = useState('Last 7 Days');
    const [searchParams, setSearchParams] = useSearchParams();
    const [tutorial, setTutorial] = useState(0);
    const timezone = userProfile?.store?.settings?.timezone;
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const numFormat = new Intl.NumberFormat('en-US');
    const dollarFormat = new Intl.NumberFormat('en-US', {style: "currency", currency: "USD", maximumFractionDigits: 2});
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const [match, setMatch] = useState({ start, end });
    const seededMatchForTz = useRef(null);

    const processLineGraphData = (data) => {
        const processedData = {};
        for (const v of data) {
            if (!v.Date) continue;

            if (!processedData[v.Date]) {
                processedData[v.Date] = { Date: v.Date, Earned: 0, Redeemed: 0 };
            }

            if (v.Action === 'Reward Redeemed') {
                processedData[v.Date].Redeemed += -v.Points;
            } else {
                processedData[v.Date].Earned += v.Points;
            }
        }
        return Object.values(processedData);
    };

    const formatSalesData = async (data, rewards) => {
        let formatted = [];
        for await (let rew of Object.keys(rewards)) { // if more than 5, start grouping into Others
            let formattedReward = {};
            let singleReward = data.filter(event => event.Reward === rew);
            if (singleReward.length > 0) {
                for (let event of singleReward) {
                    formattedReward.name = rewards[event.Reward];
                    if (event.Action === 'Reward Redeemed') formattedReward.Redemptions = event.Count;
                    if (event.Action === 'Order Placed') {
                        formattedReward.Uses = event.Count;
                        formattedReward.Subtotal = event.Subtotal;
                    }
                }
                if (!formattedReward.Uses) formattedReward.Uses = 0;
                if (!formattedReward.Subtotal) formattedReward.Subtotal = 0;
                if (!formattedReward.Redemptions) formattedReward.Redemptions = 0;
            }
            if (Object.keys(formattedReward).length > 0) formatted.push(formattedReward);
        };
        setSalesData(formatted.sort((a, b) => b.Redemptions - a.Redemptions));
    }

    const updateDemoData = async () => {
        const data = demoDashboardData();
        setEvents(data.points);
        const aggregatedData = processLineGraphData(data.points);
        
        let lineData = await fillDates(
            aggregatedData,
            match.start,
            match.end,
            {
                Earned: 0,
                Redeemed: 0
            },
            timezone
        );
        lineData.sort((a, b) => new Date(a.Date) - new Date(b.Date));
        setLineGraph(lineData);
        formatSalesData(data.rewards, data.all_rewards);
    }
    useEffect(() => {
        switch(tutorial) {
            case 2:
                updateDemoData();
                break;
            default:
                break;
        }
    }, [tutorial])

    useEffect(() => {
        const tutorialParam = searchParams.get('tutorial');
        if (tutorialParam === 'true') setTutorial(1);
    }, []);

    useEffect(() => {
        if (!timezone || seededMatchForTz.current === timezone) return;
        seededMatchForTz.current = timezone;
        setMatch(storeMatchRangeLastNCalendarDays(timezone, 7));
        setActiveRange('Last 7 Days');
    }, [timezone]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (tutorial < 2 && timezone) {
                try {
                    const token = await getJwtToken();
                    const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(
                        timezone,
                        match.start,
                        match.end
                    );

                    const res = await axios.get(
                        'https://api.amplifyloyalty.com/app/dashboard',
                        {
                            headers: {
                                Authorization: `Bearer ${token}`
                            },
                            params: {
                                start: matchStart,
                                end: matchEnd,
                                timezone
                            }
                        }
                    );
                    
                    setEvents(res.data.points);
                    const aggregatedData = processLineGraphData(res.data.points);

                    let lineData = await fillDates(
                        aggregatedData,
                        match.start,
                        match.end,
                        {
                            Earned: 0,
                            Redeemed: 0
                        },
                        timezone
                    );
                    lineData.sort((a, b) => new Date(a.Date) - new Date(b.Date));
                    setLineGraph(lineData);

                    formatSalesData(res.data.rewards, res.data.all_rewards);
                } catch (err) {
                    console.log(err);
                }
            }
        };

        fetchDashboardData();
    }, [match, timezone, tutorial]);

    const safeAOV = () => {
        const totalUses = salesData.reduce((acc, v) => acc + v.Uses, 0);
        if (totalUses === 0) return 0;
        const totalSubtotal = salesData.reduce((acc, v) => acc + v.Subtotal, 0);
        return totalSubtotal / totalUses;
    }

    return (
        <div className="dashboard">
            {tutorial === 1 && 
                <InitiateTutorial 
                    text="In this guide, you will learn how to use the Dashboard with example customer data to analyze the performance of your program."
                    header="Dashboard" 
                    setTutorial={setTutorial}
                />
            }
            {tutorial > 1 && <div className="popup-overlay"></div>}

            <div className="dashboard__filters float-element">
                <div className="dashboard__title">Program Overview</div>
                <div
                    className="date-filter promotions-table-filters__filter-wrapper"
                    style={tutorial === 6 ? {
                        position: "relative", 
                        zIndex: "55", 
                        backgroundColor: "#fff",
                        boxShadow: "0 0 10px #fff"
                    } : {}}
                >
                    <StoreDateRangeFilter 
                    active={activeRange}
                    setActive={setActiveRange}
                    timezone={timezone}
                    match={match}
                    options={[
                    {
                        text: 'Last 7 Days',
                        action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 7))
                    },
                    {
                        text: 'This Week',
                        action: () => setMatch(storeMatchRangeThisWeek(timezone))
                    },
                    {
                        text: 'Last Week',
                        action: () => setMatch(storeMatchRangeLastWeek(timezone))
                    },
                    {
                        text: 'Last 14 Days',
                        action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 14))
                    },
                    {
                        text: 'Last 30 Days',
                        action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 30))
                    },
                    {
                        text: 'This Month',
                        action: () => setMatch(storeMatchRangeThisMonth(timezone))
                    },
                    {
                        text: 'Last Month',
                        action: () => setMatch(storeMatchRangeLastMonth(timezone))
                    },
                    {
                        text: 'This Year',
                        action: () => setMatch(storeMatchRangeThisYear(timezone))
                    },
                    {
                        text: 'Last Year',
                        action: () => setMatch(storeMatchRangeLastYear(timezone))
                    },
                    {
                        text: 'Custom Range',
                        action: (start, end) => setMatch({start, end})
                    }
                    ]}
                    tutorial={tutorial}
                    setTutorial={setTutorial}
                    tutorialStart={6}
                    finish={async () => {
                        const btn = document.querySelector('.action-description__close');
                        if (!btn.disabled) {
                            btn.disabled = true;
                            try {
                                const token = await getJwtToken();
                                const res = await axios.put(
                                    'https://api.amplifyloyalty.com/app/stores/onboarding',
                                    {section: "dashboard"},
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`
                                        }
                                    }
                                );
                                setSearchParams({});
                                window.location.reload();
                            } catch (err) {
                                console.log(err);
                            }
                        }
                    }}
                />
                </div>
            </div>
            <div className="dashboard__row" style={{position: "relative"}}>
                {tutorial === 3 && 
                    <ActionDescription 
                        text={`Use the line grapgh to see how your program is trending over time. Hover over it to get details on specific dates.`}
                        style={{width: "235px", bottom: "-135px", right:"calc(50% - 313px)"}}
                        triangle={{top: "-18px", left: "calc(50% - 9px)"}}
                        tutorial={tutorial}
                        setTutorial={setTutorial}
                        next="Next"
                        hidePopup={true}
                    />
                }
                <div className="dashboard__scorecards float-element" style={tutorial === 2 ? {
                     position: "relative", 
                     zIndex: "55", 
                     backgroundColor: "#fff",
                     boxShadow: "0 0 10px #fff"
                } : {}}>
                    {tutorial === 2 && 
                        <ActionDescription 
                            text={`These are scorecards to give you an overview of how your overall program is doing.`}
                            style={{width: "210px", right: "-250px", bottom:"calc(50% - 50px)"}}
                            triangle={{left: "-18px", top: "calc(50% - 9px)", transform: "rotate(-90deg)"}}
                            tutorial={tutorial}
                            setTutorial={setTutorial}
                            next="Next"
                            hidePopup={true}
                        />
                    }
                    {events && 
                        <>
                            <Scorecard className="dashboard__scorecard" title="Points Earned" 
                                tooltip={{
                                    message: "Test",
                                    height: '300px',
                                    width: '500px'
                                }}
                                data={numFormat.format(events.reduce((acc, v) => {
                                    if (v.Action !== "Reward Redeemed") return acc + v.Points;
                                    else return acc + 0;
                                }, 0))}
                            />
                            <Scorecard className="dashboard__scorecard" title="Points Redeemed" data={numFormat.format(events.reduce((acc, v) => {
                                if (v.Action === "Reward Redeemed") return acc - v.Points;
                                else return acc + 0;
                            }, 0))}/>
                            <Scorecard className="dashboard__scorecard" title="Redemptions" data={Math.round(numFormat.format(events.reduce((acc, v) => {
                                if (v.Action === "Reward Redeemed") return acc + v.Count;
                                else return acc + 0;
                            }, 0)))}/>
                            <Scorecard className="dashboard__scorecard" title="$ Discounted" data={dollarFormat.format(events.reduce((acc,v) => acc+v.Discounted, 0))}/>
                            <Scorecard className="dashboard__scorecard" title="$ from Redemptions" data={dollarFormat.format(salesData.reduce((acc, v) => acc+v.Subtotal, 0))}/>
                            <Scorecard className="dashboard__scorecard" title="AOV w/ Redemption" data={dollarFormat.format(safeAOV())}/>
                        </>
                    }
                </div>
                <LineGraph 
                    width="100%"
                    height={250} 
                    data={lineGraph}
                    heading="Points Earned x Points Redeemed"
                    className="float-element dashboard-line-graph"
                    keys={["Earned", "Redeemed"]}
                    formatter={numFormat}
                    tickFormatter={(value) => {
                        const splitValue = value.split('-')
                        return `${parseInt(splitValue[1])}/${parseInt(splitValue[2])}`
                    }}
                    labelFormatter={(value) => {
                        const splitValue = value.split('-')
                        return `${parseInt(splitValue[1])}/${parseInt(splitValue[2])}/${splitValue[0]}`;
                    }}
                    style={tutorial === 3 ? {
                        position: "relative", 
                        zIndex: "55", 
                        backgroundColor: "#fff",
                        boxShadow: "0 0 10px #fff" 
                    } : {}}
                />
            </div>
            <div className="dashboard__row" style={{position: "relative"}}>
                {tutorial === 4 && 
                    <ActionDescription 
                        text={`View how each of your active coupons are performing based on times they were redeemed for points and times they were used in a purchase.`}
                        style={{width: "275px", top: "-115px", left:"calc(30% - 5px - 147.5px)"}}
                        triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
                        tutorial={tutorial}
                        setTutorial={setTutorial}
                        next="Next"
                        hidePopup={true}
                    />
                }
                {tutorial === 5 && 
                    <ActionDescription 
                        text={`Out of all Redemptions made, hover to see a breakdown of sales by coupon.`}
                        style={{width: "220px", top: "-100px", right:"calc(20% - 5px - 120px)"}}
                        triangle={{bottom: "-18px", left: "calc(50% - 9px)", transform: "rotate(180deg)"}}
                        tutorial={tutorial}
                        setTutorial={setTutorial}
                        next="Next"
                        hidePopup={true}
                    />
                }
                <BarGraph 
                    className="dashboard-bar-graph float-element"
                    heading="Times Redeemed / Times Used by Redemption"
                    data={salesData}
                    width="100%"
                    height={250}
                    name="name"
                    fill={['#103E61', '#F51504']}
                    firstKey="Redemptions"
                    secondKey="Uses"
                    style={tutorial === 4 ? {
                        position: "relative", 
                        zIndex: "55", 
                        backgroundColor: "#fff",
                        boxShadow: "0 0 10px #fff" 
                    }: {}}
                />
                <PieGraph 
                    className="dashboard-pie-graph float-element"
                    heading="Redemptions by $ in Sales"
                    data={[...salesData].filter(data => data.Subtotal > 0).sort((a,b) => b.Subtotal - a.Subtotal)} 
                    width="100%" 
                    height={250}
                    value="Subtotal"
                    name="name" 
                    fill={['#F8A305','#F51504', '#103E61']}
                    formatter={dollarFormat}
                    style={tutorial === 5 ? {
                        position: "relative", 
                        zIndex: "55", 
                        backgroundColor: "#fff",
                        boxShadow: "0 0 10px #fff" 
                    }: {}}
                />
            </div>
        </div>
    );
}